import React, { useState, useEffect } from 'react';
import { 
  Trophy, Crown, Star, Gift, TrendingUp, Users, Target,
  ChevronRight, Award, Zap
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const MyRanks = () => {
  const [rankData, setRankData] = useState(null);
  const [allRanks, setAllRanks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [myRewards, setMyRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('progress');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rankRes, allRanksRes, leaderRes, offersRes, rewardsRes] = await Promise.all([
        api.get('/ranks/my-rank'),
        api.get('/ranks/all-ranks'),
        api.get('/ranks/leaderboard?type=referrals&limit=10'),
        api.get('/ranks/active-offers'),
        api.get('/ranks/my-rewards')
      ]);
      setRankData(rankRes.data);
      setAllRanks(allRanksRes.data);
      setLeaderboard(leaderRes.data);
      setActiveOffers(offersRes.data);
      setMyRewards(rewardsRes.data);
    } catch (error) {
      console.error('Failed to fetch rank data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="my-ranks">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
            Ranks & Rewards
          </h1>
          <p className="text-muted-foreground">Track your progress and earn rewards</p>
        </div>

        {/* Current Rank Card */}
        {rankData?.current_rank ? (
          <div 
            className="glass-card p-6 mb-8 border-2"
            style={{ borderColor: rankData.current_rank.color + '60' }}
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="text-center">
                <span className="text-6xl">{rankData.current_rank.icon}</span>
                <h2 
                  className="text-2xl font-bold mt-2"
                  style={{ color: rankData.current_rank.color }}
                >
                  {rankData.current_rank.name}
                </h2>
                <p className="text-muted-foreground">Level {rankData.current_rank.level}</p>
              </div>
              
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox label="Referrals" value={rankData.stats.referral_count} icon={Users} />
                <StatBox label="Team Size" value={rankData.stats.team_size} icon={Users} />
                <StatBox label="Team Sales" value={`₹${rankData.stats.team_sales}`} icon={TrendingUp} />
                <StatBox label="Personal Sales" value={`₹${rankData.stats.personal_sales}`} icon={Target} />
              </div>
            </div>
            
            {rankData.current_rank.benefits?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="font-semibold mb-2">Your Benefits:</h4>
                <div className="flex flex-wrap gap-2">
                  {rankData.current_rank.benefits.map((benefit, i) => (
                    <span key={i} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="glass-card p-6 mb-8 text-center">
            <Crown className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Start Your Journey</h2>
            <p className="text-muted-foreground">
              Refer friends and make sales to unlock your first rank!
            </p>
          </div>
        )}

        {/* Progress to Next Rank */}
        {rankData?.next_rank && rankData?.progress && (
          <div className="glass-card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Progress to {rankData.next_rank.name}</h3>
              <span className="text-2xl">{rankData.next_rank.icon}</span>
            </div>
            
            <div className="space-y-4">
              {Object.entries(rankData.progress).map(([key, data]) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{key.replace('_', ' ')}</span>
                    <span>{data.current} / {data.required}</span>
                  </div>
                  <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-yellow-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, data.percentage)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6">
          {['progress', 'leaderboard', 'rewards', 'offers'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 capitalize transition-colors ${
                activeTab === tab 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* All Ranks */}
        {activeTab === 'progress' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allRanks.map((rank, idx) => {
              const isCurrentRank = rankData?.current_rank?.id === rank.id;
              const isAchieved = rankData?.current_rank?.level >= rank.level;
              
              return (
                <div 
                  key={rank.id}
                  className={`glass-card p-4 ${isCurrentRank ? 'ring-2' : ''} ${isAchieved ? '' : 'opacity-60'}`}
                  style={{ borderColor: isCurrentRank ? rank.color : 'transparent' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl">{rank.icon}</span>
                    <div>
                      <h4 className="font-bold" style={{ color: rank.color }}>{rank.name}</h4>
                      <p className="text-xs text-muted-foreground">Level {rank.level}</p>
                    </div>
                    {isCurrentRank && (
                      <span className="ml-auto px-2 py-0.5 bg-primary/20 text-primary rounded text-xs">
                        Current
                      </span>
                    )}
                    {isAchieved && !isCurrentRank && (
                      <span className="ml-auto text-green-500">✓</span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Referrals:</span> {rank.min_referrals}</p>
                    <p><span className="text-muted-foreground">Team Sales:</span> ₹{rank.min_team_sales}</p>
                    <p><span className="text-muted-foreground">Monthly Bonus:</span> <span className="text-green-500">₹{rank.monthly_bonus}</span></p>
                    <p><span className="text-muted-foreground">Commission Boost:</span> <span className="text-primary">+{rank.commission_boost}%</span></p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Leaderboard */}
        {activeTab === 'leaderboard' && (
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4">Rank</th>
                  <th className="text-left p-4">Member</th>
                  <th className="text-right p-4">Score</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="p-4">
                      <span className={`text-xl ${
                        idx === 0 ? 'text-yellow-500' :
                        idx === 1 ? 'text-gray-400' :
                        idx === 2 ? 'text-amber-600' : ''
                      }`}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${entry.rank}`}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="font-medium">{entry.name}</p>
                      <p className="text-xs text-muted-foreground">@{entry.username}</p>
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-bold">{entry.value}</span>
                      <span className="text-xs text-muted-foreground ml-1">{entry.label}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* My Rewards */}
        {activeTab === 'rewards' && (
          <div>
            {myRewards.length > 0 ? (
              <div className="space-y-3">
                {myRewards.map(reward => (
                  <div key={reward.id} className="glass-card p-4 flex items-center gap-4">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <Gift className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{reward.reward_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Earned on {new Date(reward.earned_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-500 font-bold">
                        {reward.reward_type === 'cash' ? `₹${reward.reward_value}` : reward.reward_value}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize">{reward.reward_type}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Gift className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Rewards Yet</h3>
                <p className="text-muted-foreground">Complete milestones to earn rewards!</p>
              </div>
            )}
          </div>
        )}

        {/* Active Offers */}
        {activeTab === 'offers' && (
          <div>
            {activeOffers.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {activeOffers.map(offer => (
                  <div key={offer.id} className="glass-card p-6 border-2 border-primary/30">
                    {offer.banner_image && (
                      <img src={offer.banner_image} alt="" className="w-full h-32 object-cover rounded-lg mb-4" />
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-lg">{offer.title}</h3>
                      <Zap className="w-6 h-6 text-yellow-500" />
                    </div>
                    <p className="text-muted-foreground mb-4">{offer.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-green-500">
                        {offer.bonus_type === 'percentage' ? `${offer.bonus_value}%` : `₹${offer.bonus_value}`}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Ends {new Date(offer.end_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Star className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Active Offers</h3>
                <p className="text-muted-foreground">Check back later for exciting offers!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, icon: Icon }) => (
  <div className="text-center p-3 bg-white/5 rounded-lg">
    <Icon className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
    <p className="text-xl font-bold">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

export default MyRanks;
